import { assertPence, type Pence } from './money.js';

/** Store credit top-ups (CLAUDE.md §4). The bonus is a placeholder from the prototype. */
export interface TopupOption {
  pricePence: Pence;
  bonusPence: Pence;
}

export const TOPUP_OPTIONS: readonly TopupOption[] = [
  { pricePence: 1000, bonusPence: 0 },
  { pricePence: 2000, bonusPence: 0 },
  { pricePence: 5000, bonusPence: 500 },
];

export class CreditError extends Error {
  override name = 'CreditError';
}

/** Top-up options typed in the admin: each a whole amount of pence, bonus never more than the amount, at most six, no duplicates. */
export function validateTopupOptions(raw: unknown): TopupOption[] {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > 6) throw new CreditError('Between one and six top-up amounts');
  const out: TopupOption[] = raw.map((o) => {
    const r = (o ?? {}) as { pricePence?: unknown; bonusPence?: unknown };
    if (typeof r.pricePence !== 'number' || !Number.isInteger(r.pricePence) || r.pricePence < 100 || r.pricePence > 100000) throw new CreditError('Each top-up must be between £1 and £1,000');
    const bonus = r.bonusPence ?? 0;
    if (typeof bonus !== 'number' || !Number.isInteger(bonus) || bonus < 0 || bonus > r.pricePence) throw new CreditError('A bonus must be a whole number of pence, never more than the top-up itself');
    return { pricePence: r.pricePence, bonusPence: bonus };
  });
  if (new Set(out.map((o) => o.pricePence)).size !== out.length) throw new CreditError('Each top-up amount once');
  return out.sort((a, b) => a.pricePence - b.pricePence);
}

/** Only the listed amounts can be bought. Returns what gets added to the balance. */
export function topupCredit(pricePence: Pence, options: readonly TopupOption[] = TOPUP_OPTIONS): { chargePence: Pence; creditAddedPence: Pence } {
  assertPence(pricePence);
  const opt = options.find((o) => o.pricePence === pricePence);
  if (!opt) throw new CreditError(`£${(pricePence / 100).toFixed(2)} is not a top-up amount`);
  return { chargePence: opt.pricePence, creditAddedPence: opt.pricePence + opt.bonusPence };
}

/**
 * Split an order total between store credit and card.
 * Credit is used first, up to the balance; the rest goes on the card.
 * Nothing is ever charged to Stripe for the credit part.
 */
export function splitPayment(totalPence: Pence, creditBalancePence: Pence, useCredit: boolean): { creditPence: Pence; cardPence: Pence } {
  assertPence(totalPence, 'total');
  assertPence(creditBalancePence, 'credit balance');
  if (!useCredit) return { creditPence: 0, cardPence: totalPence };
  const creditPence = Math.min(creditBalancePence, totalPence);
  return { creditPence, cardPence: totalPence - creditPence };
}

/** New balance after spending credit. Refuses to go negative. */
export function debitCredit(balancePence: Pence, amountPence: Pence): Pence {
  assertPence(balancePence, 'balance');
  assertPence(amountPence, 'amount');
  if (amountPence > balancePence) throw new CreditError('Not enough store credit');
  return balancePence - amountPence;
}

export function creditBalanceAfter(balancePence: Pence, amountPence: Pence): Pence {
  assertPence(balancePence, 'balance');
  assertPence(amountPence, 'amount');
  return balancePence + amountPence;
}
